import { brandInfo } from '@/mocks/brand';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';

export default function AboutPage() {
  const seoTitle = '关于蜜芽｜母婴跨境电商平台品牌介绍';
  const seoDesc = '蜜芽是中国领先的母婴跨境电商平台，成立于2014年，汇聚全球3000+优质母婴品牌，服务超过5000万中国家庭。了解蜜芽品牌故事与核心优势。';
  const seoKeywords = '蜜芽品牌,蜜芽介绍,母婴跨境电商,蜜芽优势,关于蜜芽';
  const canonicalUrl = 'https://childcart.github.io/about';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '蜜芽的商品都是正品吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的。蜜芽所有商品均与品牌方或授权经销商直接合作，建立严格的供应商审核机制和正品保障体系，承诺100%正品，假一赔十。',
        },
      },
      {
        '@type': 'Question',
        name: '蜜芽支持哪些支付方式？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蜜芽支持微信支付、支付宝、银行卡等多种支付方式，为您提供安全便捷的购物体验。',
        },
      },
      {
        '@type': 'Question',
        name: '蜜芽的跨境商品多久能到货？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '跨境商品通常在7-15个工作日内送达，具体时效取决于商品来源地和清关速度。蜜芽在日韩、澳洲、欧洲、北美等地设有直采中心，致力于缩短跨境物流时间。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系蜜芽客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系蜜芽客服：拨打客服热线 400-888-6688，或发送邮件至 service@mia.com。客服工作时间：周一至周日 9:00-21:00。',
        },
      },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '蜜芽母婴跨境电商平台',
        url: canonicalUrl,
        foundingDate: '2014',
        description: brandInfo.mission,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '望京SOHO T1-B座 2001-2010',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          addressCountry: 'CN',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: brandInfo.contact.phone,
          email: brandInfo.contact.email,
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      },
      {
        '@type': 'WebPage',
        name: seoTitle,
        description: seoDesc,
        url: canonicalUrl,
        isPartOf: { '@type': 'WebSite', name: '蜜芽', url: 'https://childcart.github.io' },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={canonicalUrl}
        ogType="website"
        schema={aboutSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="px-4 md:px-6 pb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                关于蜜芽
              </h1>
            </div>
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>{brandInfo.description.split('\n\n')[0]}</p>
                  <p>{brandInfo.description.split('\n\n')[1]}</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20modern%20office%20interior%20with%20natural%20light%2C%20clean%20minimalist%20workspace%20with%20wooden%20furniture%2C%20cream%20and%20beige%20tones%2C%20professional%20yet%20cozy%20atmosphere&width=800&height=600&seq=about-brand-mia&orientation=landscape"
                  alt="蜜芽品牌办公室"
                  className="w-full h-full object-cover"
                  title="蜜芽品牌"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              核心优势
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10">为每一位妈妈提供值得信赖的母婴购物体验</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {brandInfo.values.map((val) => (
                <div key={val.title} className="bg-background-50 rounded-xl p-6 text-center hover:shadow-sm transition-all duration-300">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${val.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-600">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="honors" className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              品牌荣誉
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10">用实力与口碑赢得信赖</p>

            <div className="max-w-3xl mx-auto">
              <div className="relative pl-8 border-l-2 border-primary-200">
                {brandInfo.honors.map((honor, index) => (
                  <div key={index} className="relative pb-8 last:pb-0">
                    <div className="absolute -left-[calc(1rem+5px)] top-0 w-4 h-4 rounded-full bg-primary-500 border-2 border-background-50"></div>
                    <div className="bg-background-100 rounded-xl p-5">
                      <span className="text-xs font-bold text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full">{honor.year}</span>
                      <h3 className="text-base font-semibold text-foreground-900 mt-2 mb-1">{honor.title}</h3>
                      <p className="text-sm text-foreground-600">{honor.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10">期待与您的每一次相遇</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-xl p-6 text-center hover:shadow-sm transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-phone-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                <a href={`tel:${brandInfo.contact.phone}`} className="text-accent-600 font-semibold hover:text-accent-700 transition-colors duration-200">
                  {brandInfo.contact.phone}
                </a>
                <p className="text-xs text-foreground-500 mt-1">{brandInfo.contact.workingHours}</p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 text-center hover:shadow-sm transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-mail-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <a href={`mailto:${brandInfo.contact.email}`} className="text-accent-600 font-medium hover:text-accent-700 transition-colors duration-200 text-sm">
                  {brandInfo.contact.email}
                </a>
                <p className="text-xs text-foreground-500 mt-1">工作日24小时内回复</p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 text-center hover:shadow-sm transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-map-pin-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                <p className="text-sm text-foreground-600">{brandInfo.contact.address}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: "var(--font-heading)" }}>
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '蜜芽的商品都是正品吗？', a: '是的。蜜芽所有商品均与品牌方或授权经销商直接合作，建立严格的供应商审核机制和正品保障体系，承诺100%正品，假一赔十。' },
                { q: '蜜芽支持哪些支付方式？', a: '蜜芽支持微信支付、支付宝、银行卡等多种支付方式，为您提供安全便捷的购物体验。' },
                { q: '蜜芽的跨境商品多久能到货？', a: '跨境商品通常在7-15个工作日内送达，具体时效取决于商品来源地和清关速度。蜜芽在日韩、澳洲、欧洲、北美等地设有直采中心，致力于缩短跨境物流时间。' },
                { q: '如何联系蜜芽客服？', a: '您可以通过客服热线 400-888-6688 或发送邮件至 service@mia.com 联系我们。客服工作时间：周一至周日 9:00-21:00。' },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-200 transition-colors duration-200">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}